import { createRoot, hydrateRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./i18n/config";
import "./index.css";

// Prefetch critical routes + register service worker after initial load (production only).
// The SW uses network-first for HTML so new deploys are NOT shadowed by stale cache.
// We only register on the production custom domain, never on the Lovable preview iframe
// (id-preview-*.lovable.app / *.lovable.app) to keep the editor preview fresh.
if (import.meta.env.PROD && typeof window !== "undefined") {
  window.addEventListener("load", () => {
    const idle =
      (window as Window & { requestIdleCallback?: (cb: () => void) => void })
        .requestIdleCallback || ((cb: () => void) => setTimeout(cb, 1));
    idle(() => {
      const prefetchLinks = [
        "/cabo-verde-islands-guide",
        "/expert-led-tours",
        "/contact-book-tour-cabo-verde",
      ];
      prefetchLinks.forEach((href) => {
        const link = document.createElement("link");
        link.rel = "prefetch";
        link.href = href;
        document.head.appendChild(link);
      });

      // Register service worker only on the production custom domain.
      const host = window.location.hostname;
      const isProdHost =
        host === "sploratours.com" || host === "www.sploratours.com";
      if (isProdHost && "serviceWorker" in navigator) {
        navigator.serviceWorker
          .register("/sw.js", { scope: "/" })
          .catch(() => {
            /* ignore SW registration failure */
          });
      } else if (!isProdHost && "serviceWorker" in navigator) {
        // Defensive: if a previous SW was registered on a preview host, unregister it.
        navigator.serviceWorker.getRegistrations().then((regs) => {
          regs.forEach((r) => r.unregister());
        });
      }
    });
  });
}

const container = document.getElementById("root")!;
const tree = (
  <HelmetProvider>
    <App />
  </HelmetProvider>
);

// If the HTML was statically prerendered (Netlify build), hydrate over the
// existing markup so bots/AI crawlers see real content immediately and users
// get instant first paint. Otherwise, do a normal client render.
const isPrerendered =
  document.documentElement.dataset.prerendered === "true" &&
  container.hasChildNodes();

if (isPrerendered) {
  hydrateRoot(container, tree);
} else {
  createRoot(container).render(tree);
}

// Signal readiness for the prerender script.
requestAnimationFrame(() => {
  requestAnimationFrame(() => {
    (window as unknown as { __APP_READY__?: boolean }).__APP_READY__ = true;
  });
});
